/**
 * midi-to-notes.js — parse a MIDI file into the note shape the chiptune /
 * audio-to-notes engines already speak, so a MIDI can drive them DIRECTLY,
 * with no lossy audio→notes transcription in between.
 *
 * Why this exists: a chiptune converter that rejects MIDI is backwards — MIDI
 * *is* note data, exactly what BasicPitchEngine spends 8–32 s trying to
 * recover from audio (imperfectly). Handed a MIDI, we already have perfect
 * notes: parse them, voice them across the NES channels, done — instant and
 * pitch-accurate. (Historically chiptune and MIDI/tracker formats are the same
 * world.) The shared uploader signposts .mid to /midi by default; a tool opts
 * out via `accept-midi` on <x-tool-uploader> and calls this instead.
 *
 * Uses the @tonejs/midi global (`Midi`, /vendor/midi/Midi.min.js) — the same
 * parser the /midi tool loads. Caller must ensure it's present.
 *
 *   var res = window.MidiToNotes.parse(arrayBuffer);
 *   // res = { notes:    [{startTimeSeconds, durationSeconds, pitchMidi, amplitude,
 *   //                     bends?:[{t, semi}]}],   // t = seconds from note start
 *   //         percussion: [{time, strength, kind:'kick'|'hat'}],
 *   //         duration:  seconds, trackCount, drumCount }
 *
 * Pitch bends: MIDI carries continuous pitch bends per track (track.pitchBends,
 * value −1..1). A slide/vibrato is a core chiptune gesture and the NES 2A03
 * changes a channel's frequency register mid-note natively, so we DON'T drop
 * them: each note gets the bend curve active during its span, in semitones,
 * and the render modulates the oscillator frequency from it. Default bend range
 * is ±2 semitones (General MIDI RPN-0 default; @tonejs/midi does not surface a
 * per-file sensitivity, and ±2 is right for the overwhelming majority).
 *
 * Style: var/function ES5 to match public/js and stay parseable pre-Chrome-80.
 */
(function(){
'use strict';

// GM default pitch-bend range: the wheel's full ±1 spans ±2 semitones.
var BEND_RANGE_SEMITONES = 2;

// General MIDI percussion is channel 10 (index 9). @tonejs/midi also flags it
// via track.instrument.percussion. Detect either way.
function isDrumTrack(track){
    if (!track) return false;
    if (track.channel === 9) return true;
    if (track.instrument && track.instrument.percussion) return true;
    return false;
}

// GM drum map → the two voices the NES noise channel can suggest: a low thump
// (kick) vs a bright tick (snare/hat/cymbal). 35/36 are the bass drums.
function drumKind(midi){
    return (midi === 35 || midi === 36 || midi === 41 || midi === 43) ? 'kick' : 'hat';
}

// Build a note-relative bend curve (in semitones) from a track's pitch-bend
// events. `bends` is [{time, value}] (value −1..1), assumed sorted by time.
// Returns null when the note sees no meaningful bend, so unbent notes keep the
// engine's fast fixed-frequency path. The point at t=0 carries the wheel's
// value AT note-onset (a bend set before the note still applies).
function bendCurveForNote(bends, noteStart, noteEnd){
    if (!bends || !bends.length) return null;
    var EPS = 1e-6;
    var init = 0;
    for (var i = 0; i < bends.length; i++) {
        var bt = bends[i].time || 0;
        if (bt <= noteStart + EPS) init = bends[i].value || 0;
        else break;
    }
    var pts = [{ t: 0, semi: init * BEND_RANGE_SEMITONES }];
    for (var j = 0; j < bends.length; j++) {
        var t = bends[j].time || 0;
        if (t > noteStart + EPS && t < noteEnd - EPS) {
            pts.push({ t: t - noteStart, semi: (bends[j].value || 0) * BEND_RANGE_SEMITONES });
        }
    }
    var moved = false;
    for (var k = 0; k < pts.length; k++) { if (Math.abs(pts[k].semi) > 0.02) { moved = true; break; } }
    return moved ? pts : null;
}

// Core: takes an already-parsed @tonejs/midi object. parse() wraps this over
// an ArrayBuffer; keeping it separate makes the mapping unit-testable with an
// in-memory Midi (whose pitch bends survive — a round-tripped toArray() does
// not re-serialize them, a @tonejs/midi write-path quirk).
function parseMidi(midi){
    var notes = [], percussion = [];
    var tracks = (midi && midi.tracks) || [];
    var drumCount = 0, melodicTracks = 0, maxEnd = 0;

    for (var t = 0; t < tracks.length; t++) {
        var track = tracks[t];
        var tn = (track && track.notes) || [];
        if (!tn.length) continue;

        if (isDrumTrack(track)) {
            drumCount++;
            for (var d = 0; d < tn.length; d++) {
                var dn = tn[d];
                percussion.push({
                    time: dn.time || 0,
                    strength: Math.max(0.2, Math.min(1, dn.velocity != null ? dn.velocity : 0.7)),
                    kind: drumKind(dn.midi | 0),
                });
                var de = (dn.time || 0) + (dn.duration || 0);
                if (de > maxEnd) maxEnd = de;
            }
            continue;
        }

        melodicTracks++;
        // Pitch bends are per-track; sort once, then attach the relevant slice
        // to each note. (@tonejs/midi exposes pb.time as a getter in seconds.)
        var pbs = ((track && track.pitchBends) || []).map(function(pb){
            return { time: pb.time || 0, value: pb.value || 0 };
        }).sort(function(a, b){ return a.time - b.time; });

        for (var n = 0; n < tn.length; n++) {
            var nn = tn[n];
            var start = nn.time || 0;
            var dur = Math.max(0.03, nn.duration || 0);
            var note = {
                startTimeSeconds: start,
                durationSeconds: dur,
                pitchMidi: nn.midi | 0,
                amplitude: nn.velocity != null ? Math.max(0.05, Math.min(1, nn.velocity)) : 0.7,
            };
            if (pbs.length) {
                var curve = bendCurveForNote(pbs, start, start + dur);
                if (curve) note.bends = curve;
            }
            notes.push(note);
            if (start + dur > maxEnd) maxEnd = start + dur;
        }
    }

    notes.sort(function(a, b){ return a.startTimeSeconds - b.startTimeSeconds; });
    percussion.sort(function(a, b){ return a.time - b.time; });

    var duration = (midi && midi.duration) || maxEnd || 0;
    return {
        notes: notes, percussion: percussion, duration: duration,
        trackCount: melodicTracks, drumCount: drumCount,
    };
}

function parse(arrayBuffer){
    if (typeof Midi === 'undefined' && !(window && window.Midi)) {
        throw new Error('midi_parser_missing');
    }
    var Ctor = (typeof Midi !== 'undefined') ? Midi : window.Midi;
    /* The one door our tools read a .mid through (midi-stream-notes.js):
       @tonejs/midi alone turns a release written before a strike into a
       zero-length strike. A page that forgot the tag still parses, as before. */
    return parseMidi(window.MidiStreamNotes ? window.MidiStreamNotes.parse(Ctor, arrayBuffer) : new Ctor(arrayBuffer));
}

window.MidiToNotes = { parse: parse, parseMidi: parseMidi, isDrumTrack: isDrumTrack, bendCurveForNote: bendCurveForNote };

})();
